import { useState, useEffect, useRef, useCallback, useContext } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import GlobalContext from '../context/GlobalContext.js'
import getTenantTaskInstance from '../functions/API/getTenantTaskInstance.js'
import postUpdateFieldValue from '../functions/API/postUpdateFieldValue.js'
import postUpdateStepStatus from '../functions/API/postUpdateStepStatus.js'
import postInitiateUpload from '../functions/API/postInitiateUpload.js'
import postUploadChunk from '../functions/API/postUploadChunk.js'
import postCompleteUpload from '../functions/API/postCompleteUpload.js'
import postInitiateStepPayment from '../functions/API/postInitiateStepPayment.js'
import postAPI from '../functions/API/postAPI.js'
import { toast } from '../lib/toast.js'
import {
  ArrowLeft, Upload, FileText, CheckCircle2, AlertCircle, CreditCard, X, Info, ExternalLink, FileSignature,
} from 'lucide-react'

// --- Helpers (matching mobile app logic) ---

function getExistingValue(field) {
  switch (field.field_type_snapshot) {
    case 'BOOLEAN': return field.value_boolean
    case 'DATE': return field.value_date || null
    case 'NUMBER':
    case 'CURRENCY': return field.value_number != null ? String(field.value_number) : ''
    case 'FILE': return field.value_file_url || ''
    case 'DROPDOWN':
      if (field.value_json) {
        try { return typeof field.value_json === 'string' ? JSON.parse(field.value_json) : field.value_json }
        catch { return null }
      }
      return null
    case 'MULTI_SELECT':
      if (field.value_json) {
        try {
          const v = typeof field.value_json === 'string' ? JSON.parse(field.value_json) : field.value_json
          return Array.isArray(v) ? v : []
        } catch { return [] }
      }
      return []
    default: return field.value_text || ''
  }
}

function getFieldOptions(field) {
  const rules = field.validation_rules_snapshot
  if (!rules) return []
  const parsed = typeof rules === 'string' ? JSON.parse(rules) : rules
  return parsed?.options || []
}

function getValidationRules(field) {
  const rules = field.validation_rules_snapshot
  if (!rules) return {}
  return typeof rules === 'string' ? JSON.parse(rules) : rules
}

function validateFieldRules(field, value) {
  const rules = getValidationRules(field)
  const type = field.field_type_snapshot
  const errors = []

  if (value === null || value === undefined || value === '') return errors

  if (type === 'TEXT' || type === 'TEXTAREA') {
    const str = String(value)
    if (rules.min_length && str.length < rules.min_length) errors.push(`Must be at least ${rules.min_length} characters`)
    if (rules.max_length && str.length > rules.max_length) errors.push(`Must be no more than ${rules.max_length} characters`)
    if (rules.pattern) {
      try {
        if (!new RegExp(rules.pattern).test(str)) errors.push(rules.pattern_message || 'Invalid format')
      } catch { /* ignore invalid regex */ }
    }
  }

  if (type === 'NUMBER' || type === 'CURRENCY') {
    const num = parseFloat(value)
    if (isNaN(num)) {
      errors.push('Must be a valid number')
    } else {
      if (rules.min_value != null && num < rules.min_value) errors.push(`Must be at least ${rules.min_value}`)
      if (rules.max_value != null && num > rules.max_value) errors.push(`Must be no more than ${rules.max_value}`)
      if (type === 'NUMBER' && rules.allow_decimals === false && !Number.isInteger(num)) errors.push('Decimals are not allowed')
    }
  }

  if (type === 'DATE' && value) {
    const d = new Date(value)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    if (rules.allow_past === false && d < today) errors.push('Past dates are not allowed')
    if (rules.allow_future === false && d > today) errors.push('Future dates are not allowed')
    if (rules.min_date && d < new Date(rules.min_date)) errors.push(`Date must be on or after ${new Date(rules.min_date).toLocaleDateString('en-GB')}`)
    if (rules.max_date && d > new Date(rules.max_date)) errors.push(`Date must be on or before ${new Date(rules.max_date).toLocaleDateString('en-GB')}`)
  }

  if (type === 'MULTI_SELECT' && Array.isArray(value)) {
    if (rules.min_selections && value.length < rules.min_selections) errors.push(`Select at least ${rules.min_selections}`)
    if (rules.max_selections && value.length > rules.max_selections) errors.push(`Select no more than ${rules.max_selections}`)
  }

  return errors
}

export default function OnboardingStepDetail() {
  const { stepId } = useParams()
  const navigate = useNavigate()
  const { settings } = useContext(GlobalContext)
  const primaryColour = settings?.primary_hex_colour || '#1F2937'

  const [loading, setLoading] = useState(true)
  const [instance, setInstance] = useState(null)
  const [step, setStep] = useState(null)
  const [fieldValues, setFieldValues] = useState({})
  const [submitting, setSubmitting] = useState(false)

  // Document upload state
  const [pickedFiles, setPickedFiles] = useState([])       // files selected but not yet uploaded
  const [uploadState, setUploadState] = useState('idle')    // 'idle' | 'uploading' | 'completed' | 'error'
  const [fileProgress, setFileProgress] = useState({})      // { [index]: { status, progress } }
  const fileInputRef = useRef(null)
  const [dragOver, setDragOver] = useState(false)

  // Payment state
  const [paymentLoading, setPaymentLoading] = useState(false)
  const [paymentModal, setPaymentModal] = useState(null)
  const [paymentResult, setPaymentResult] = useState(null) // 'success' | 'cancelled' | 'error'

  // Document signing state
  const [signingLoading, setSigningLoading] = useState(false)

  useEffect(() => {
    loadStep()
  }, [stepId])

  async function loadStep() {
    // We need to find which instance has this step - fetch all instances first
    try {
      const { default: getTenantTaskInstances } = await import('../functions/API/getTenantTaskInstances.js')
      const instResponse = await getTenantTaskInstances()
      const instances = instResponse.data?.task_instances || []
      if (!instances.length) {
        navigate('/onboarding')
        return
      }

      const inst = instances[0]
      const detailResponse = await getTenantTaskInstance(inst.id)
      if (!detailResponse.response?.ok || !detailResponse.data?.task_instance) {
        toast.error('Failed to load step')
        navigate('/onboarding')
        return
      }

      const fullInstance = detailResponse.data.task_instance
      setInstance(fullInstance)

      const foundStep = fullInstance.steps?.find(s => String(s.id) === String(stepId))
      if (!foundStep) {
        toast.error('Step not found')
        navigate('/onboarding')
        return
      }

      setStep(foundStep)

      // Initialize field values
      const initial = {}
      ;(foundStep.fields || []).forEach(field => {
        initial[field.id] = getExistingValue(field)
      })
      setFieldValues(initial)

      // If document step is already completed, mark upload state
      if (foundStep.step_type_snapshot === 'document_upload' && foundStep.status === 'COMPLETED') {
        setUploadState('completed')
      }

      setLoading(false)
    } catch (e) {
      toast.error('Failed to load step')
      navigate('/onboarding')
    }
  }

  function updateLocalValue(fieldId, value) {
    setFieldValues(prev => ({ ...prev, [fieldId]: value }))
  }

  // --- Standard Step: Save ---

  async function handleSaveStandard() {
    const sortedFields = [...(step.fields || [])]
      .sort((a, b) => (a.position_snapshot || 0) - (b.position_snapshot || 0))
      .filter(f => f.field_type_snapshot !== 'FILE')

    // Validate required
    const missingFields = sortedFields.filter(f => {
      if (!f.is_required_snapshot) return false
      const val = fieldValues[f.id]
      if (f.field_type_snapshot === 'BOOLEAN') return val !== true && val !== false
      if (val === null || val === undefined || val === '') return true
      if (Array.isArray(val) && val.length === 0) return true
      return false
    })

    if (missingFields.length > 0) {
      const names = missingFields.map(f => f.name_snapshot).join(', ')
      toast.error(`Please complete: ${names}`)
      return
    }

    // Validate field rules
    for (const field of sortedFields) {
      const errors = validateFieldRules(field, fieldValues[field.id])
      if (errors.length > 0) {
        toast.error(`${field.name_snapshot}: ${errors[0]}`)
        return
      }
    }

    setSubmitting(true)

    // Save all field values in parallel
    const savePromises = sortedFields.map(f => {
      const val = fieldValues[f.id]
      if (val === undefined) return Promise.resolve()
      return postUpdateFieldValue({ id: f.id, value: val })
    })
    await Promise.all(savePromises)

    // Submit step as completed
    const response = await postUpdateStepStatus({ id: step.id, status: 'COMPLETED' })
    if (response.response?.ok) {
      toast.success('Step completed')
      navigate('/onboarding')
    } else {
      toast.error(typeof response.data?.message === 'string' ? response.data.message : 'Failed to submit step')
    }

    setSubmitting(false)
  }

  // --- Document Upload ---

  function handleFileSelect(files) {
    if (!files?.length) return
    const config = step.step_config_snapshot || {}
    const allowedTypes = config.allowed_types || []
    const maxSizeMb = config.max_size_mb || 50
    const maxFiles = config.max_files || 5

    for (const file of files) {
      if (pickedFiles.length >= maxFiles) {
        toast.error(`You can upload up to ${maxFiles} files`)
        break
      }

      // Check file type
      if (allowedTypes.length > 0) {
        const ext = file.name.split('.').pop().toLowerCase()
        if (!allowedTypes.some(t => t.toLowerCase() === ext)) {
          toast.error(`File type .${ext} is not allowed. Allowed: ${allowedTypes.map(t => '.' + t).join(', ')}`)
          continue
        }
      }

      // Check file size
      if (file.size > maxSizeMb * 1024 * 1024) {
        toast.error(`${file.name} exceeds the ${maxSizeMb}MB limit`)
        continue
      }

      setPickedFiles(prev => [...prev, { file, name: file.name, size: file.size }])
    }
  }

  function removePickedFile(index) {
    setPickedFiles(prev => prev.filter((_, i) => i !== index))
    setFileProgress(prev => {
      const updated = { ...prev }
      delete updated[index]
      return updated
    })
  }

  function readFileAsBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result.split(',')[1])
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }

  async function handleUploadAndComplete() {
    if (pickedFiles.length === 0 || uploadState === 'uploading') return

    setSubmitting(true)
    setUploadState('uploading')

    try {
      // Upload each file: initiate → chunks
      for (let i = 0; i < pickedFiles.length; i++) {
        const { file, name, size } = pickedFiles[i]
        setFileProgress(prev => ({ ...prev, [i]: { status: 'uploading', progress: 0 } }))

        // 1. Read file as base64
        const base64Data = await readFileAsBase64(file)

        // 2. Initiate upload
        const initRes = await postInitiateUpload({
          task_instance_step_id: step.id,
          filename: name,
          file_size: size,
          file_index: i,
        })

        if (!initRes.response?.ok) {
          const msg = initRes.data?.message
          toast.error(typeof msg === 'string' ? msg : 'Failed to start upload')
          setFileProgress(prev => ({ ...prev, [i]: { status: 'error', progress: 0 } }))
          setUploadState('error')
          setSubmitting(false)
          return
        }

        const { file_id, chunk_size } = initRes.data
        const chunkSizeBytes = chunk_size || 4 * 1024 * 1024
        const base64ChunkSize = Math.ceil(chunkSizeBytes / 3) * 4
        const totalChunks = Math.ceil(base64Data.length / base64ChunkSize)

        // 3. Upload each chunk
        for (let c = 0; c < totalChunks; c++) {
          const chunkStart = c * base64ChunkSize
          const chunkEnd = Math.min(chunkStart + base64ChunkSize, base64Data.length)
          const chunkData = base64Data.substring(chunkStart, chunkEnd)

          const chunkRes = await postUploadChunk({
            task_instance_step_id: step.id,
            file_id,
            chunk_data: chunkData,
            chunk_index: c,
          })

          if (!chunkRes.response?.ok) {
            toast.error('Upload failed')
            setFileProgress(prev => ({ ...prev, [i]: { status: 'error', progress: 0 } }))
            setUploadState('error')
            setSubmitting(false)
            return
          }

          const progress = Math.round(((c + 1) / totalChunks) * 100)
          setFileProgress(prev => ({ ...prev, [i]: { status: 'uploading', progress } }))
        }

        // Mark file as scanning (chunks done, awaiting server scan)
        setFileProgress(prev => ({ ...prev, [i]: { status: 'scanning', progress: 100 } }))
      }

      // 4. Complete upload (triggers server-side scan + SharePoint upload)
      const completeRes = await postCompleteUpload({
        task_instance_step_id: step.id,
      })

      // Handle rejected files from security scanning
      if (completeRes.response?.status === 422) {
        const rejectedFiles = completeRes.data?.rejected_files || []
        const rejectedByName = {}
        for (const rf of rejectedFiles) {
          rejectedByName[rf.filename] = rf.reason === 'malicious'
            ? 'This file could not be uploaded'
            : 'Upload failed — please retry'
        }
        setFileProgress(prev => {
          const updated = { ...prev }
          for (let i = 0; i < pickedFiles.length; i++) {
            const reason = rejectedByName[pickedFiles[i].name]
            if (reason) {
              updated[i] = { status: 'rejected', progress: 100, error: reason }
            } else if (updated[i]?.status === 'scanning') {
              updated[i] = { status: 'done', progress: 100 }
            }
          }
          return updated
        })
        toast.error('Some files were rejected')
        setUploadState('error')
        setSubmitting(false)
        return
      }

      if (!completeRes.response?.ok) {
        toast.error(typeof completeRes.data?.message === 'string' ? completeRes.data.message : 'Failed to complete upload')
        setFileProgress(prev => {
          const updated = { ...prev }
          for (const key of Object.keys(updated)) {
            if (updated[key]?.status === 'scanning') {
              updated[key] = { status: 'error', progress: 100 }
            }
          }
          return updated
        })
        setUploadState('error')
        setSubmitting(false)
        return
      }

      // Success
      setFileProgress(prev => {
        const updated = { ...prev }
        for (const key of Object.keys(updated)) {
          if (updated[key]?.status === 'scanning') {
            updated[key] = { status: 'done', progress: 100 }
          }
        }
        return updated
      })
      setUploadState('completed')
      toast.success('Step completed')
      navigate('/onboarding')
    } catch (e) {
      toast.error('Upload failed')
      setUploadState('error')
      setSubmitting(false)
    }
  }

  // Drag and drop handlers
  const handleDragOver = useCallback((e) => {
    e.preventDefault()
    setDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e) => {
    e.preventDefault()
    setDragOver(false)
  }, [])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    setDragOver(false)
    handleFileSelect(e.dataTransfer.files)
  }, [step])

  // --- Payment Step ---

  async function handleInitiatePayment() {
    setPaymentLoading(true)
    const response = await postInitiateStepPayment({ task_instance_step_id: step.id })

    if (!response.response?.ok) {
      toast.error(typeof response.data?.message === 'string' ? response.data.message : 'Failed to initiate payment')
      setPaymentLoading(false)
      return
    }

    const { token, client_code, gateway_url, amount, customer_name } = response.data
    const reason = step.name_snapshot || 'Onboarding Payment'
    const customerRef = customer_name || 'Unknown'

    const params = {
      token,
      clientcode: client_code,
      gatewayurl: gateway_url,
      customerReference: customerRef,
      amount: amount || 0,
      paymentReason: reason,
    }

    setPaymentResult(null)
    setPaymentModal(params)

    // Submit the AllPay form directly into the iframe (same as home.js)
    setTimeout(() => {
      const form = document.createElement('form')
      form.method = 'POST'
      form.action = params.gatewayurl
      form.target = 'OnboardingPaymentIFrame'
      const fields = {
        token: params.token,
        customerReference: params.customerReference,
        amount: params.amount,
        clientCode: params.clientcode,
        paymentReason: params.paymentReason,
      }
      Object.entries(fields).forEach(([k, v]) => {
        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = k
        input.value = v
        form.appendChild(input)
      })
      document.body.appendChild(form)
      form.submit()
      document.body.removeChild(form)
    }, 100)

    setPaymentLoading(false)
  }

  // Listen for AllPay payment completion messages
  useEffect(() => {
    function handlePaymentMessage(event) {
      if (!paymentModal) return
      if (event.data.resultCode !== undefined) {
        if (event.data.resultCode === 0) {
          setPaymentResult('success')
        } else if (event.data.resultCode < 0) {
          setPaymentResult('cancelled')
        } else {
          setPaymentResult('error')
        }
      }
    }
    window.addEventListener('message', handlePaymentMessage)
    return () => window.removeEventListener('message', handlePaymentMessage)
  }, [paymentModal])

  async function closePaymentModal() {
    if (paymentResult === 'success') {
      // Call the payment complete endpoint (matching mobile app)
      try {
        await postAPI('taskinstances/steps/payment/complete', {
          task_instance_step_id: step.id,
          transaction_id: 'allpay-' + step.id + '-' + Date.now(),
          status: 'success',
        })
        toast.success('Payment completed')
      } catch (e) {
        // Still navigate back even if complete call fails
      }
      navigate('/onboarding')
    }
    setPaymentModal(null)
    setPaymentResult(null)
  }

  // --- Info Step ---

  async function handleCompleteInfo() {
    setSubmitting(true)
    const response = await postUpdateStepStatus({ id: step.id, status: 'COMPLETED' })
    if (response.response?.ok) {
      toast.success('Step completed')
      navigate('/onboarding')
    } else {
      toast.error(typeof response.data?.message === 'string' ? response.data.message : 'Failed to complete step')
    }
    setSubmitting(false)
  }

  // --- Document Signing ---

  async function handleOpenSigningDocument() {
    setSigningLoading(true)
    // Open the window synchronously so Safari doesn't block it as a popup
    const newWindow = window.open('', '_blank')
    try {
      const response = await postAPI('taskinstances/steps/signing/check', {
        task_instance_step_id: step.id,
      })
      if (response.response?.ok && response.data?.signing_url) {
        newWindow.location.href = response.data.signing_url
      } else {
        newWindow.close()
        toast.error('Unable to get signing link')
      }
    } catch (e) {
      newWindow.close()
      toast.error('Failed to open document')
    }
    setSigningLoading(false)
  }

  async function handleCheckSigningStatus() {
    setSigningLoading(true)
    try {
      const response = await postAPI('taskinstances/steps/signing/check', {
        task_instance_step_id: step.id,
      })
      if (response.response?.ok && response.data) {
        const status = response.data.status
        if (status === 'signed') {
          toast.success('Document signed successfully')
          navigate('/onboarding')
        } else if (status === 'draft') {
          toast.info('Document is being prepared')
        } else if (status === 'sent' || status === 'waiting') {
          if (response.data.current_signer === 'admin') {
            toast.success(`You've signed the document. Waiting for ${settings?.company_name || 'counter'}-signature`)
            navigate('/onboarding')
          } else {
            toast.info('Awaiting signature')
          }
        } else if (status === 'cancelled' || status === 'declined') {
          toast.error('Signing was ' + status)
        }
      } else {
        const msg = response.data?.message
        toast.error(typeof msg === 'string' ? msg : 'Failed to check signing status')
      }
    } catch (e) {
      toast.error('Failed to check signing status')
    }
    setSigningLoading(false)
  }

  // --- Multi-select toggle ---

  function handleMultiToggle(fieldId, optionValue) {
    const current = fieldValues[fieldId] || []
    const updated = current.includes(optionValue)
      ? current.filter(v => v !== optionValue)
      : [...current, optionValue]
    updateLocalValue(fieldId, updated)
  }

  // --- Render ---

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: primaryColour }}></div>
      </div>
    )
  }

  if (!step) return null

  const sortedFields = step.step_type_snapshot === 'standard'
    ? [...(step.fields || [])]
        .sort((a, b) => (a.position_snapshot || 0) - (b.position_snapshot || 0))
        .filter(f => f.field_type_snapshot !== 'FILE')
    : []

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: primaryColour }}>
      {/* Header */}
      <div>
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center">
          <button
            onClick={() => navigate('/onboarding')}
            className="flex items-center gap-1.5 text-sm text-white/70 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <h1 className="flex-1 text-center text-base font-semibold text-white pr-12">
            {step.name_snapshot}
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto bg-white rounded-t-[24px] shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
        <div className="max-w-2xl mx-auto px-4 pt-6 pb-6">

          {/* ===== STANDARD STEP ===== */}
          {step.step_type_snapshot === 'standard' && (
            <>
              {step.description_snapshot && (
                <p className="text-sm text-gray-500 text-center mb-6">
                  {step.description_snapshot}
                </p>
              )}

              <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-200 p-6">
                <div className="space-y-5">
                  {sortedFields.map(field => {
                    const type = field.field_type_snapshot
                    const required = field.is_required_snapshot
                    const label = field.name_snapshot + (required ? ' *' : '')
                    const placeholder = field.placeholder_snapshot || ''
                    const helpText = field.help_text_snapshot
                    const helpUrl = field.help_url_snapshot
                    const rules = getValidationRules(field)

                    return (
                      <div key={field.id}>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          {label}
                        </label>

                        {type === 'TEXT' && (
                          <input
                            type="text"
                            value={fieldValues[field.id] || ''}
                            onChange={(e) => updateLocalValue(field.id, e.target.value)}
                            placeholder={placeholder || 'Enter text'}
                            maxLength={rules.max_length || undefined}
                            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent"
                          />
                        )}

                        {type === 'TEXTAREA' && (
                          <textarea
                            value={fieldValues[field.id] || ''}
                            onChange={(e) => updateLocalValue(field.id, e.target.value)}
                            placeholder={placeholder || 'Enter details'}
                            maxLength={rules.max_length || undefined}
                            rows={4}
                            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent resize-none"
                          />
                        )}

                        {type === 'NUMBER' && (
                          <input
                            type="number"
                            value={fieldValues[field.id] != null ? String(fieldValues[field.id]) : ''}
                            onChange={(e) => updateLocalValue(field.id, e.target.value)}
                            placeholder={placeholder || '0'}
                            step={rules.allow_decimals === false ? '1' : 'any'}
                            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent"
                          />
                        )}

                        {type === 'CURRENCY' && (
                          <div className="flex">
                            <span className="inline-flex items-center px-3.5 rounded-l-xl border border-r-0 border-gray-200 bg-gray-100 text-sm font-medium text-gray-600">
                              £
                            </span>
                            <input
                              type="number"
                              value={fieldValues[field.id] != null ? String(fieldValues[field.id]) : ''}
                              onChange={(e) => updateLocalValue(field.id, e.target.value)}
                              placeholder={placeholder || '0.00'}
                              step="0.01"
                              className="flex-1 rounded-r-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent"
                            />
                          </div>
                        )}

                        {type === 'DATE' && (
                          <input
                            type="date"
                            value={fieldValues[field.id] || ''}
                            onChange={(e) => updateLocalValue(field.id, e.target.value)}
                            onInput={(e) => updateLocalValue(field.id, e.target.value)}
                            onBlur={(e) => updateLocalValue(field.id, e.target.value)}
                            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent"
                          />
                        )}

                        {type === 'BOOLEAN' && (
                          <div className="flex gap-3">
                            {[
                              { value: true, label: 'Yes' },
                              { value: false, label: 'No' },
                            ].map(opt => {
                              const isSelected = fieldValues[field.id] === opt.value
                              return (
                                <button
                                  key={String(opt.value)}
                                  onClick={() => updateLocalValue(field.id, opt.value)}
                                  className={`flex-1 rounded-xl border-2 px-4 py-3 text-sm font-medium transition-colors ${
                                    isSelected
                                      ? 'text-white'
                                      : 'border-gray-200 bg-gray-50 text-gray-700 hover:border-gray-300'
                                  }`}
                                  style={isSelected ? { borderColor: primaryColour, backgroundColor: primaryColour } : undefined}
                                >
                                  {opt.label}
                                </button>
                              )
                            })}
                          </div>
                        )}

                        {type === 'DROPDOWN' && (() => {
                          const options = getFieldOptions(field)
                          return (
                            <div className="space-y-2">
                              {options.map(opt => {
                                const isSelected = fieldValues[field.id] === opt.value
                                return (
                                  <button
                                    key={opt.value}
                                    onClick={() => updateLocalValue(field.id, opt.value)}
                                    className={`w-full flex items-center gap-3 rounded-xl border-2 px-4 py-3 text-sm text-left transition-colors ${
                                      isSelected
                                        ? 'bg-gray-50'
                                        : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                                    }`}
                                    style={isSelected ? { borderColor: primaryColour } : undefined}
                                  >
                                    <div
                                      className="h-4 w-4 rounded-full border-2 flex items-center justify-center"
                                      style={{ borderColor: isSelected ? primaryColour : '#D1D5DB' }}
                                    >
                                      {isSelected && <div className="h-2 w-2 rounded-full" style={{ backgroundColor: primaryColour }} />}
                                    </div>
                                    <span className={isSelected ? 'font-medium text-gray-900' : 'text-gray-700'}>
                                      {opt.label}
                                    </span>
                                  </button>
                                )
                              })}
                            </div>
                          )
                        })()}

                        {type === 'MULTI_SELECT' && (() => {
                          const options = getFieldOptions(field)
                          const selected = fieldValues[field.id] || []
                          const maxSel = rules.max_selections
                          const atMax = maxSel && selected.length >= maxSel
                          return (
                            <div className="space-y-2">
                              {maxSel && (
                                <p className="text-xs text-gray-400 mb-1">Select up to {maxSel}</p>
                              )}
                              {options.map(opt => {
                                const isSelected = selected.includes(opt.value)
                                const disabled = atMax && !isSelected
                                return (
                                  <button
                                    key={opt.value}
                                    onClick={() => !disabled && handleMultiToggle(field.id, opt.value)}
                                    disabled={disabled}
                                    className={`w-full flex items-center gap-3 rounded-xl border-2 px-4 py-3 text-sm text-left transition-colors ${
                                      isSelected
                                        ? 'bg-gray-50'
                                        : disabled
                                          ? 'border-gray-100 bg-gray-50 opacity-40'
                                          : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                                    }`}
                                    style={isSelected ? { borderColor: primaryColour } : undefined}
                                  >
                                    <div
                                      className="h-4 w-4 rounded border-2 flex items-center justify-center"
                                      style={isSelected ? { borderColor: primaryColour, backgroundColor: primaryColour } : { borderColor: '#D1D5DB' }}
                                    >
                                      {isSelected && (
                                        <svg className="h-3 w-3 text-white" viewBox="0 0 12 12" fill="none">
                                          <path d="M2.5 6L5 8.5L9.5 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                      )}
                                    </div>
                                    <span className={isSelected ? 'font-medium text-gray-900' : 'text-gray-700'}>
                                      {opt.label}
                                    </span>
                                  </button>
                                )
                              })}
                            </div>
                          )
                        })()}

                        {(helpText || helpUrl) && (
                          <div className="flex items-center gap-2 mt-1">
                            {helpText && <p className="text-xs text-gray-400">{helpText}</p>}
                            {helpUrl && (
                              <a href={helpUrl} target="_blank" rel="noopener noreferrer" title="Help" className="text-gray-400 hover:text-gray-600">
                                <ExternalLink className="h-3.5 w-3.5" />
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Save Button */}
              <div className="mt-6">
                <button
                  onClick={handleSaveStandard}
                  disabled={submitting}
                  className="w-full rounded-xl px-4 py-3.5 text-sm font-semibold text-white disabled:opacity-50"
                  style={{ backgroundColor: primaryColour }}
                >
                  {submitting ? 'Saving...' : 'Save'}
                </button>
              </div>
            </>
          )}

          {/* ===== DOCUMENT UPLOAD STEP ===== */}
          {step.step_type_snapshot === 'document_upload' && (
            <>
              {step.description_snapshot && (
                <p className="text-sm text-gray-500 text-center mb-6">
                  {step.description_snapshot}
                </p>
              )}
              {(step.help_text_snapshot || step.help_url_snapshot) && (
                <div className="flex items-center gap-2 justify-center mb-4">
                  {step.help_text_snapshot && <p className="text-xs text-gray-400">{step.help_text_snapshot}</p>}
                  {step.help_url_snapshot && (
                    <a href={step.help_url_snapshot} target="_blank" rel="noopener noreferrer" title="Help" className="text-gray-400 hover:text-gray-600">
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>
              )}

              <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-200 p-6">
                {/* Drop zone - only show when not uploading */}
                {uploadState !== 'uploading' && (
                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                      dragOver
                        ? 'bg-gray-50'
                        : 'border-gray-200 hover:border-gray-300 bg-gray-50'
                    }`}
                    style={dragOver ? { borderColor: primaryColour } : undefined}
                  >
                    <Upload className="h-8 w-8 text-gray-400 mx-auto mb-3" />
                    <p className="text-sm font-medium text-gray-700">
                      Click to upload or drag and drop
                    </p>
                    {(() => {
                      const config = step.step_config_snapshot || {}
                      const types = config.allowed_types?.map(t => t.toUpperCase()).join(', ') || 'Any file type'
                      const maxSize = config.max_size_mb || 50
                      return (
                        <p className="text-xs text-gray-400 mt-1">
                          {types} up to {maxSize}MB
                        </p>
                      )
                    })()}
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      onChange={(e) => { handleFileSelect(e.target.files); e.target.value = '' }}
                      className="hidden"
                      accept={step.step_config_snapshot?.allowed_types?.map(t => '.' + t).join(',') || undefined}
                    />
                  </div>
                )}

                {/* Picked file list */}
                {pickedFiles.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {pickedFiles.map((file, idx) => {
                      const progress = fileProgress[idx]
                      return (
                        <div
                          key={idx}
                          className="flex items-center gap-3 rounded-xl bg-gray-50 px-4 py-3"
                        >
                          <FileText className="h-5 w-5 text-gray-400 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-700 truncate">{file.name}</p>
                            <p className="text-xs text-gray-400">
                              {file.size ? (file.size < 1024 * 1024 ? `${(file.size / 1024).toFixed(0)}KB` : `${(file.size / 1024 / 1024).toFixed(1)}MB`) : ''}
                              {progress?.status === 'uploading' && progress.progress > 0 && ` · ${progress.progress}%`}
                            </p>
                          </div>
                          {!progress && uploadState !== 'uploading' && (
                            <button onClick={(e) => { e.stopPropagation(); removePickedFile(idx) }} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                              <X className="h-4 w-4" />
                            </button>
                          )}
                          {progress?.status === 'uploading' && (
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-400 flex-shrink-0"></div>
                          )}
                          {progress?.status === 'scanning' && (
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                              <span className="text-xs text-gray-400">Scanning</span>
                            </div>
                          )}
                          {progress?.status === 'done' && (
                            <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                          )}
                          {(progress?.status === 'error' || progress?.status === 'rejected') && (
                            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>

              {/* Upload & Submit Button */}
              <div className="mt-6">
                <button
                  onClick={handleUploadAndComplete}
                  disabled={submitting || pickedFiles.length === 0 || uploadState === 'uploading'}
                  className="w-full rounded-xl px-4 py-3.5 text-sm font-semibold text-white disabled:opacity-50"
                  style={{ backgroundColor: primaryColour }}
                >
                  {uploadState === 'uploading' ? 'Uploading...' : `Upload & Submit${pickedFiles.length > 0 ? ` (${pickedFiles.length})` : ''}`}
                </button>
              </div>
            </>
          )}

          {/* ===== PAYMENT STEP ===== */}
          {step.step_type_snapshot === 'payment' && (
            <>
              <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-200 p-8 text-center">
                <div
                  className="mx-auto flex h-16 w-16 items-center justify-center rounded-full mb-4"
                  style={{ backgroundColor: primaryColour + '12' }}
                >
                  <CreditCard className="h-8 w-8" style={{ color: primaryColour }} />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Payment Required</h2>
                {step.step_config_snapshot?.amount && (
                  <p className="text-3xl font-bold mt-2" style={{ color: primaryColour }}>
                    £{parseFloat(step.step_config_snapshot.amount).toFixed(2)}
                  </p>
                )}
                {step.description_snapshot && (
                  <p className="text-sm text-gray-500 mt-3">{step.description_snapshot}</p>
                )}
                {(step.help_text_snapshot || step.help_url_snapshot) && (
                  <div className="flex items-center gap-2 justify-center mt-2">
                    {step.help_text_snapshot && <p className="text-xs text-gray-400">{step.help_text_snapshot}</p>}
                    {step.help_url_snapshot && (
                      <a href={step.help_url_snapshot} target="_blank" rel="noopener noreferrer" title="Help" className="text-gray-400 hover:text-gray-600">
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </div>
                )}

                <button
                  onClick={handleInitiatePayment}
                  disabled={paymentLoading}
                  className="mt-6 w-full rounded-xl px-4 py-3.5 text-sm font-semibold text-white disabled:opacity-50"
                  style={{ backgroundColor: primaryColour }}
                >
                  {paymentLoading ? 'Processing...' : 'Make Payment'}
                </button>
              </div>
            </>
          )}

          {/* ===== DOCUMENT SIGNING STEP ===== */}
          {step.step_type_snapshot === 'document_signing' && (() => {
            const hasAgreement = !!step.step_result?.agreement_id && step.step_result?.status !== 'cancelled' && step.step_result?.status !== 'draft'
            const companyName = settings?.company_name || 'us'

            return (
              <>
                <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-200 p-8 text-center">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-amber-50 mb-4">
                    <FileSignature className="h-7 w-7 text-amber-600" />
                  </div>

                  {!hasAgreement ? (
                    <>
                      <h2 className="text-lg font-semibold text-gray-900">Waiting for document to be prepared</h2>
                      <p className="text-sm text-gray-500 mt-3">We're preparing your document for signing. You'll be able to sign it once it's ready.</p>
                    </>
                  ) : step.step_result?.current_signer === 'admin' ? (
                    <>
                      <h2 className="text-lg font-semibold text-gray-900">Waiting for {companyName} to sign</h2>
                      {step.step_result?.agreement_name && (
                        <p className="text-sm text-gray-400 mt-1">{step.step_result.agreement_name}</p>
                      )}
                      {step.step_result?.participants?.find(p => p.role === 'customer')?.status === 'signed' ? (
                        <p className="text-sm text-gray-500 mt-3">You've signed the document. Waiting for {companyName} to counter-sign.</p>
                      ) : (
                        <p className="text-sm text-gray-500 mt-3">{companyName} needs to sign this document before you.</p>
                      )}
                    </>
                  ) : (
                    <>
                      <h2 className="text-lg font-semibold text-gray-900">Document ready to sign</h2>
                      {step.step_result?.agreement_name && (
                        <p className="text-sm text-gray-400 mt-1">{step.step_result.agreement_name}</p>
                      )}
                      {step.description_snapshot && (
                        <p className="text-sm text-gray-500 mt-3">{step.description_snapshot}</p>
                      )}
                      <p className="text-sm text-gray-500 mt-2">Please sign the document to continue</p>
                    </>
                  )}

                  {(step.help_text_snapshot || step.help_url_snapshot) && (
                    <div className="flex items-center gap-2 justify-center mt-3">
                      {step.help_text_snapshot && <p className="text-xs text-gray-400">{step.help_text_snapshot}</p>}
                      {step.help_url_snapshot && (
                        <a href={step.help_url_snapshot} target="_blank" rel="noopener noreferrer" title="Help" className="text-gray-400 hover:text-gray-600">
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                    </div>
                  )}
                </div>

                {/* Buttons: customer can sign */}
                {hasAgreement && step.step_result?.current_signer !== 'admin' && (
                  <div className="mt-6 space-y-3">
                    <button
                      onClick={handleOpenSigningDocument}
                      disabled={signingLoading}
                      className="w-full rounded-xl px-4 py-3.5 text-sm font-semibold text-white disabled:opacity-50"
                      style={{ backgroundColor: primaryColour }}
                    >
                      {signingLoading ? 'Loading...' : 'Sign Document'}
                    </button>
                    <button
                      onClick={handleCheckSigningStatus}
                      disabled={signingLoading}
                      className="w-full rounded-xl px-4 py-3.5 text-sm font-semibold border-2 disabled:opacity-50"
                      style={{ borderColor: primaryColour, color: primaryColour }}
                    >
                      {signingLoading ? 'Checking...' : "I've Signed This"}
                    </button>
                  </div>
                )}

                {/* Buttons: waiting for admin */}
                {hasAgreement && step.step_result?.current_signer === 'admin' && (
                  <div className="mt-6">
                    <button
                      onClick={handleCheckSigningStatus}
                      disabled={signingLoading}
                      className="w-full rounded-xl px-4 py-3.5 text-sm font-semibold border-2 disabled:opacity-50"
                      style={{ borderColor: primaryColour, color: primaryColour }}
                    >
                      {signingLoading ? 'Checking...' : 'Check Status'}
                    </button>
                  </div>
                )}
              </>
            )
          })()}

          {/* ===== INFORMATION STEP ===== */}
          {(step.step_type_snapshot === 'information' || step.step_type_snapshot === 'info') && (
            <>
              <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-200 p-8">
                <div
                  className="mx-auto flex h-14 w-14 items-center justify-center rounded-full mb-4"
                  style={{ backgroundColor: primaryColour + '12' }}
                >
                  <Info className="h-7 w-7" style={{ color: primaryColour }} />
                </div>
                <h2 className="text-lg font-semibold text-gray-900 text-center">{step.name_snapshot}</h2>
                {step.description_snapshot && (
                  <p className="text-sm text-gray-600 mt-3 whitespace-pre-wrap leading-relaxed">
                    {step.description_snapshot}
                  </p>
                )}
                {(step.help_text_snapshot || step.help_url_snapshot) && (
                  <div className="flex items-center gap-2 mt-3">
                    {step.help_text_snapshot && <p className="text-xs text-gray-400">{step.help_text_snapshot}</p>}
                    {step.help_url_snapshot && (
                      <a href={step.help_url_snapshot} target="_blank" rel="noopener noreferrer" title="Help" className="text-gray-400 hover:text-gray-600">
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </div>
                )}
              </div>

              <div className="mt-6">
                <button
                  onClick={handleCompleteInfo}
                  disabled={submitting}
                  className="w-full rounded-xl px-4 py-3.5 text-sm font-semibold text-white disabled:opacity-50"
                  style={{ backgroundColor: primaryColour }}
                >
                  {submitting ? 'Submitting...' : step.step_type_snapshot === 'info' ? 'Acknowledge' : "I've read this"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* AllPay Payment Modal */}
      {paymentModal && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm animate-fadeIn" onClick={paymentResult ? closePaymentModal : undefined} />

          {/* Modal — bottom sheet on mobile, centered on desktop */}
          <div className="fixed inset-0 z-50 flex items-end lg:items-center lg:justify-center lg:p-4">
            <div className="w-full animate-slideUp flex flex-col rounded-t-2xl lg:rounded-2xl shadow-2xl lg:max-w-lg overflow-hidden bg-white" style={{ height: '92vh', maxHeight: '92vh' }}>
              {/* Drag handle — mobile only */}
              <div className="flex justify-center pt-3 pb-1 flex-shrink-0 lg:hidden">
                <div className="w-10 h-1 rounded-full bg-gray-300" />
              </div>
              {/* Header */}
              <div className="flex items-center justify-between px-5 pb-3 lg:pt-5 flex-shrink-0">
                <h2 className="text-lg font-bold text-gray-900">Make Payment</h2>
                {/* Mobile: text Done button */}
                <button onClick={closePaymentModal} className="text-sm font-semibold lg:hidden" style={{ color: primaryColour }}>Done</button>
                {/* Desktop: X button */}
                <button
                  onClick={closePaymentModal}
                  className="hidden lg:flex w-8 h-8 rounded-full items-center justify-center transition-colors hover:bg-gray-200"
                >
                  <X size={18} className="text-gray-400" strokeWidth={2} />
                </button>
              </div>
              {/* Content */}
              <div className="flex-1 min-h-0">
                {paymentResult ? (
                  <div className="flex flex-col items-center justify-center h-full px-5">
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${paymentResult === 'success' ? 'bg-green-100' : 'bg-red-100'}`}>
                      {paymentResult === 'success'
                        ? <CreditCard size={28} className="text-green-600" />
                        : <X size={28} className="text-red-500" />
                      }
                    </div>
                    <p className={`text-lg font-bold mb-2 ${paymentResult === 'success' ? 'text-green-600' : 'text-red-500'}`}>
                      {paymentResult === 'success' ? 'Payment Successful' : paymentResult === 'cancelled' ? 'Payment Cancelled' : 'Payment Failed'}
                    </p>
                    {paymentResult === 'success' && (
                      <p className="text-sm text-center text-gray-500">
                        Your payment has been received. This step will be marked as complete.
                      </p>
                    )}
                    <button
                      onClick={closePaymentModal}
                      className="mt-6 px-8 py-3 rounded-xl text-sm font-semibold text-white"
                      style={{ backgroundColor: primaryColour }}
                    >
                      {paymentResult === 'success' ? 'Continue' : 'Done'}
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col h-full">
                    <iframe
                      name="OnboardingPaymentIFrame"
                      id="OnboardingPaymentIFrame"
                      title="Make Payment"
                      className="w-full flex-1 border-0"
                    />
                    <p className="text-xs text-gray-400 text-center py-2.5 flex-shrink-0 border-t border-gray-100">Please do not refresh the page while your payment is being processed</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
